import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useInsurancePolicies } from '@/hooks/useInsurancePolicies';
import { usePolicyPayments } from '@/hooks/usePolicyPayments';
import { useCreatePremiumPayment } from '@/hooks/useCreatePremiumPayment';
import { useDeletePremiumPayment } from '@/hooks/useDeletePremiumPayment';
import { PaymentHistoryList } from '@/components/PaymentHistoryList';
import { PremiumPaymentForm, PremiumPaymentFormData } from '@/components/PremiumPaymentForm';
import { generatePremiumSchedule } from '@/lib/insurance-utils';
import { ScheduledPremium } from '@/types/insurance';
import { assignScheduleEntryStatus } from './scheduleStatus';

// ─── Status badge helpers ────────────────────────────────────────────────────

function PolicyStatusBadge({ status }: { status: string }) {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    active: 'default',
    renewal_due: 'secondary',
    expired: 'destructive',
  };
  const labels: Record<string, string> = {
    active: 'Active',
    renewal_due: 'Renewal Due',
    expired: 'Expired',
  };
  return (
    <Badge variant={variants[status] ?? 'outline'}>
      {labels[status] ?? status}
    </Badge>
  );
}

function ScheduleStatusBadge({ status }: { status: ScheduledPremium['status'] }) {
  if (status === 'paid') {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Paid</Badge>;
  }
  if (status === 'overdue') {
    return <Badge variant="destructive">Overdue</Badge>;
  }
  return <Badge variant="outline" className="text-blue-600 border-blue-300">Upcoming</Badge>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground sm:w-48 shrink-0">{label}</span>
      <span className="text-sm font-medium">{value ?? '—'}</span>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function InsurancePolicyDetailPage() {
  const { policyId } = useParams<{ policyId: string }>();
  const navigate = useNavigate();
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);

  const { data: policies = [], isLoading: policiesLoading } = useInsurancePolicies();
  const policy = policies.find((p) => p.id === policyId);

  const { data: payments = [], isLoading: paymentsLoading } = usePolicyPayments(policyId);
  const createPayment = useCreatePremiumPayment();
  const deletePayment = useDeletePremiumPayment(policyId!);

  // ── Premium schedule with status assignment (task 10.3) ──────────────────
  const schedule: ScheduledPremium[] = useMemo(() => {
    if (!policy) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Build a Set of recorded payment dates for O(1) lookup
    const paidDates = new Set(payments.map((p) => p.payment_date));

    const raw = generatePremiumSchedule(
      policy.start_date,
      policy.end_date,
      policy.premium_frequency as 'monthly' | 'quarterly' | 'semi_annual' | 'annual',
      policy.premium_amount,
    );

    return raw.map((entry) => ({
      ...entry,
      status: assignScheduleEntryStatus(entry.dueDate, paidDates, today),
    }));
  }, [policy, payments]);

  // ── Loading state ─────────────────────────────────────────────────────────
  if (policiesLoading) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // ── Not-found state ───────────────────────────────────────────────────────
  if (!policy) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <Shield className="h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">Policy not found</p>
            <p className="text-sm text-muted-foreground">
              The policy you're looking for doesn't exist or you don't have access to it.
            </p>
            <Button asChild variant="outline">
              <Link to="/insurance">Back to Insurance</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Handle payment form submit ────────────────────────────────────────────
  function handleRecordPayment(formData: PremiumPaymentFormData) {
    createPayment.mutate(
      {
        data: {
          policy_id: policy!.id,
          amount: formData.amount,
          currency: policy!.currency,
          payment_date: formData.payment_date,
          notes: formData.notes ?? null,
        },
        policy,
      },
      {
        onSuccess: () => setRecordPaymentOpen(false),
      },
    );
  }

  const frequencyLabel: Record<string, string> = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    semi_annual: 'Semi-Annual',
    annual: 'Annual',
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/insurance')}
          aria-label="Back to insurance list"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold truncate">{policy.provider_name}</h1>
            <PolicyStatusBadge status={policy.status} />
          </div>
          <p className="text-sm text-muted-foreground">
            {policy.policy_type} · {policy.policy_number}
          </p>
        </div>
      </div>

      {/* Policy Details (task 10.1) */}
      <Card>
        <CardHeader>
          <CardTitle>Policy Details</CardTitle>
          <CardDescription>Full information for this insurance policy</CardDescription>
        </CardHeader>
        <CardContent>
          <DetailRow label="Policy Type" value={policy.policy_type} />
          <DetailRow label="Provider" value={policy.provider_name} />
          <DetailRow label="Policy Number" value={policy.policy_number} />
          <DetailRow
            label="Coverage Amount"
            value={formatCurrency(policy.coverage_amount, policy.currency)}
          />
          <DetailRow
            label="Premium Amount"
            value={`${formatCurrency(policy.premium_amount, policy.currency)} (${frequencyLabel[policy.premium_frequency] ?? policy.premium_frequency})`}
          />
          <DetailRow label="Currency" value={policy.currency} />
          <DetailRow label="Start Date" value={formatDate(policy.start_date)} />
          <DetailRow label="End Date" value={formatDate(policy.end_date)} />
          {policy.deductible_amount != null && (
            <DetailRow
              label="Deductible"
              value={formatCurrency(policy.deductible_amount, policy.currency)}
            />
          )}
          {policy.beneficiary_name && (
            <DetailRow
              label="Beneficiary"
              value={
                policy.beneficiary_relationship
                  ? `${policy.beneficiary_name} (${policy.beneficiary_relationship})`
                  : policy.beneficiary_name
              }
            />
          )}
          {policy.provider_contact && (
            <DetailRow label="Provider Contact" value={policy.provider_contact} />
          )}
          {policy.notes && <DetailRow label="Notes" value={policy.notes} />}
          <DetailRow
            label="Days Until Expiration"
            value={
              policy.daysUntilExpiration > 0
                ? `${policy.daysUntilExpiration} days`
                : 'Expired'
            }
          />
        </CardContent>
      </Card>

      {/* Payment History + Record Payment (task 10.2) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Payment History</h2>
          <Button onClick={() => setRecordPaymentOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Record Payment
          </Button>
        </div>

        {paymentsLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <PaymentHistoryList
            payments={payments}
            onDelete={(id) => deletePayment.mutate(id)}
          />
        )}
      </div>

      {/* Record Payment Dialog */}
      <Dialog open={recordPaymentOpen} onOpenChange={setRecordPaymentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Premium Payment</DialogTitle>
          </DialogHeader>
          <PremiumPaymentForm
            policy={policy}
            onSubmit={handleRecordPayment}
            isLoading={createPayment.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Premium Schedule (task 10.3) */}
      <Card>
        <CardHeader>
          <CardTitle>Premium Schedule</CardTitle>
          <CardDescription>
            All scheduled premium due dates for the full policy term
          </CardDescription>
        </CardHeader>
        <CardContent>
          {schedule.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No scheduled premiums found for this policy.
            </p>
          ) : (
            <div className="space-y-2">
              {schedule.map((entry) => (
                <div
                  key={entry.dueDate}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <ScheduleStatusBadge status={entry.status} />
                    <span className="text-sm font-medium">{formatDate(entry.dueDate)}</span>
                  </div>
                  <span className="text-sm font-semibold">
                    {formatCurrency(entry.amount, policy.currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
